import { isCERole } from "@scoach/types";
import { useToast } from "@scoach/ui";
import { Chat, Close } from "@scoach/ui/icons";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { effectiveGeminiKey, ensureGeminiKey } from "../../lib/geminiKey.ts";
import { useAuthStore } from "../auth/store.ts";
import { dashboardApi } from "../dashboard/api.ts";
import type { AudioCaptureFrame } from "../meeting/live/audio/useAudioCapture.ts";
import { useMicCapture } from "../meeting/live/audio/useMicCapture.ts";
import { WHATS_NEW_EVENT } from "../whatsNew/WhatsNewModal.tsx";
import {
  clearHighlight,
  executeHighlight,
  executeNavigate,
  executeSwitchMeetingTab,
  readScreen,
  startHoverTracking,
  stopHoverTracking,
} from "./actionBridge.ts";
import { buildOnboardingSystemPrompt, screenContextFor } from "./screenContext.ts";
import { useOnboardingLive } from "./useOnboardingLive.ts";

import "../../styles/onboarding-agent.css";

/** Fired by the header menu item to open the guide (unhides the bubble + opens it). */
export const OPEN_ONBOARDING_AGENT_EVENT = "sally:open-onboarding-agent";

/**
 * Floating voice guide + coach. A chat bubble on every screen; clicking it starts
 * a Gemini Live voice session (on the user's own key) that can onboard the user,
 * coach them from their recent meetings, teach a topic like a private tutor, and
 * tell them what's new. It sees the screen in real time (incl. popups).
 *
 * Close (✕) just closes the panel — the bubble stays. "Hide bubble" removes the
 * bubble entirely (with a toast); it's brought back from the header chat icon.
 */
export function OnboardingAgentWidget() {
  const navigate = useNavigate();
  const toast = useToast();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hidden = useAuthStore((s) => s.onboardingBubbleHidden);
  const setHidden = useAuthStore((s) => s.setOnboardingBubbleHidden);
  const role = useAuthStore((s) => s.user?.role);
  const [open, setOpen] = useState(false);
  const [micLevel, setMicLevel] = useState(-100);

  const ctx = useMemo(() => screenContextFor(pathname), [pathname]);
  const systemInstruction = useMemo(
    () => buildOnboardingSystemPrompt(ctx, { roleLabel: role, technical: isCERole(role) }),
    [ctx, role],
  );

  const onToolCall = useCallback(
    (name: string, args: Record<string, unknown>): string => {
      if (name === "readScreen") return readScreen();
      if (name === "navigate") {
        return executeNavigate(String(args.to ?? ""), (p) => navigate({ to: p }));
      }
      if (name === "switchMeetingTab") {
        return executeSwitchMeetingTab(String(args.tab ?? ""));
      }
      if (name === "highlight") return executeHighlight(String(args.text ?? ""));
      if (name === "showWhatsNew") {
        window.dispatchEvent(new Event(WHATS_NEW_EVENT));
        return "Opened the What's new popup for the user.";
      }
      return `Unknown action "${name}".`;
    },
    [navigate],
  );

  const live = useOnboardingLive({ systemInstruction, onToolCall });
  const liveRef = useRef(live);
  useEffect(() => {
    liveRef.current = live;
  }, [live]);

  const handleFrame = useCallback((frame: AudioCaptureFrame) => {
    setMicLevel(frame.rmsDb);
    if (!liveRef.current.connected) return;
    liveRef.current.sendAudio(frame.pcm);
  }, []);

  const mic = useMicCapture({ onFrame: handleFrame, paused: !open || !live.connected });

  // Menu launcher: bring the bubble back AND open the panel.
  useEffect(() => {
    const onOpen = () => {
      setHidden(false);
      setOpen(true);
    };
    window.addEventListener(OPEN_ONBOARDING_AGENT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_ONBOARDING_AGENT_EVENT, onOpen);
  }, [setHidden]);

  // Load the user's coaching context (recent meetings + insights) once, so the
  // agent can suggest what to learn — mirrors what "My training" is fed.
  const [learningBlock, setLearningBlock] = useState("");
  const learningFetchedRef = useRef(false);
  const loadLearning = useCallback(async () => {
    if (learningFetchedRef.current) return;
    learningFetchedRef.current = true;
    try {
      const [insights, hist] = await Promise.all([
        dashboardApi.fetchInsights().catch(() => []),
        dashboardApi
          .fetchHistory({ limit: 12, light: true })
          .then((r) => r.items)
          .catch(() => []),
      ]);
      const parts: string[] = [];
      const r = useAuthStore.getState().user?.role;
      if (r) parts.push(`Role: ${r}`);
      if (hist.length) {
        parts.push(
          `Recent meetings: ${hist
            .map((m) => `${m.client} — ${m.title}`)
            .slice(0, 12)
            .join("; ")}`,
        );
      }
      if (insights.length) {
        parts.push(
          `Coaching insights: ${insights.map((i) => `${i.title}: ${i.detail}`).join(" | ")}`,
        );
      }
      setLearningBlock(parts.join("\n"));
    } catch {
      /* coaching context is best-effort */
    }
  }, []);

  // Start / stop the whole session when the panel opens / closes.
  const kickedOffRef = useRef(false);
  const learningSentRef = useRef(false);
  useEffect(() => {
    if (open) {
      if (!effectiveGeminiKey()) void ensureGeminiKey();
      startHoverTracking();
      live.connect();
      void mic.start();
      void loadLearning();
    } else {
      stopHoverTracking();
      mic.stop();
      live.disconnect();
      clearHighlight();
      setMicLevel(-100);
      kickedOffRef.current = false;
      learningSentRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Once connected: push coaching context (when ready) and kick off the spoken
  // greeting/menu. Guarded so each fires once per open session.
  useEffect(() => {
    if (!open || !live.connected) return;
    if (learningBlock && !learningSentRef.current) {
      learningSentRef.current = true;
      liveRef.current.sendContext(`[YOUR MEETINGS & COACHING CONTEXT]\n${learningBlock}`);
    }
    if (!kickedOffRef.current) {
      kickedOffRef.current = true;
      liveRef.current.sendUserText(
        "[The user just opened you. Greet them in one short sentence, then offer the menu of the four things you can do (show them around SellSynq / coach them from their recent meetings / teach a topic like a tutor / tell them what's new). Keep it brief and ask what they'd like.]",
      );
    }
  }, [open, live.connected, learningBlock]);

  // Feed the model a fresh snapshot of the screen (incl. popups) whenever the DOM
  // changes, so it "sees" what the user sees in real time — not only on request.
  const lastPushed = useRef("");
  const pushSnapshot = useCallback(() => {
    if (!liveRef.current.connected) return;
    const snap = readScreen();
    if (snap && snap !== lastPushed.current) {
      lastPushed.current = snap;
      liveRef.current.sendContext(snap);
    }
  }, []);

  useEffect(() => {
    if (!open || !live.connected) return;
    lastPushed.current = "";
    pushSnapshot();
    let t: ReturnType<typeof setTimeout> | null = null;
    const obs = new MutationObserver(() => {
      if (t) clearTimeout(t);
      t = setTimeout(pushSnapshot, 700);
    });
    obs.observe(document.body, { childList: true, subtree: true });
    return () => {
      if (t) clearTimeout(t);
      obs.disconnect();
    };
  }, [open, live.connected, pushSnapshot]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: push on route change
  useEffect(() => {
    if (open && live.connected) pushSnapshot();
  }, [pathname]);

  const hasKey = Boolean(effectiveGeminiKey());

  const micActive = micLevel > -55;
  const level = live.speaking ? 1 : micActive ? Math.min(1, (micLevel + 60) / 45) : 0.08;

  // Hidden and not open → render nothing (brought back via the header chat icon).
  if (hidden && !open) return null;

  return (
    <>
      {!hidden && (
        <button
          type="button"
          className={`oa-fab${open ? " oa-fab-open" : ""}`}
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close assistant" : "Open SellSynq guide"}
          title="SellSynq guide & coach"
        >
          {open ? <Close size={22} /> : <Chat size={26} />}
        </button>
      )}

      {open && (
        <div className="oa-panel" aria-label="SellSynq guide and coach">
          <div className="oa-head">
            <div className="oa-head-title">
              <span className="oa-dot" data-on={live.connected} />
              SellSynq guide & coach
            </div>
            <div className="oa-head-sub">{ctx.title}</div>
            <button
              type="button"
              className="oa-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
              title="Close (keeps the bubble)"
            >
              ✕
            </button>
          </div>

          <div className="oa-body">
            {!hasKey ? (
              <p className="oa-msg">Loading assistant…</p>
            ) : live.error ? (
              <div className="oa-msg oa-err" style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
                <span>{live.error}</span>
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => live.connect()}
                  style={{ fontSize: 12, padding: "3px 12px", background: "rgba(0,0,0,0.06)", borderRadius: 6 }}
                >
                  Retry connection
                </button>
              </div>
            ) : live.connecting ? (
              <p className="oa-msg">Connecting…</p>
            ) : live.connected ? (
              <p className="oa-msg">
                {live.speaking
                  ? "Speaking…"
                  : "Listening — ask about SellSynq, your meetings, or a topic to learn."}
              </p>
            ) : (
              <p className="oa-msg">Starting…</p>
            )}

            <div className={`oa-viz${live.speaking ? " oa-viz-speaking" : ""}`} aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <span
                  key={i}
                  className="oa-bar"
                  style={{
                    height: `${Math.max(6, level * 34 * (0.6 + ((i % 3) + 1) * 0.18))}px`,
                    animationDelay: `${i * 90}ms`,
                  }}
                />
              ))}
            </div>
          </div>

          <div className="oa-foot">
            <span>Onboard · coach from your meetings · teach a topic · what's new.</span>
            <button
              type="button"
              className="oa-hide-link"
              onClick={() => {
                setHidden(true);
                setOpen(false);
                toast.push({
                  tone: "info",
                  message: "Guide hidden — bring it back from the chat icon at the top.",
                });
              }}
              title="Hide the bubble — reopen it from the chat icon at the top"
            >
              Hide bubble
            </button>
          </div>
        </div>
      )}
    </>
  );
}
